import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { capitalizeFirst } from './EntriesList';

const CalendarView = ({ entries, clients, cases, selectedDate, onDateSelect }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hoverInfo, setHoverInfo] = useState(null); // { date, dayEntries, scheduledEntries, x, y, above }

  const monthNames = [
    'Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος', 'Μάιος', 'Ιούνιος',
    'Ιούλιος', 'Αύγουστος', 'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος'
  ];

  const dayNames = ['Κυρ', 'Δευ', 'Τρί', 'Τετ', 'Πέμ', 'Παρ', 'Σάβ'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add days from previous month to fill the first row
    const prevMonth = new Date(year, month, 0); // Last day of previous month
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthDays - i),
        isCurrentMonth: false
      });
    }

    // Add all days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        date: new Date(year, month, day),
        isCurrentMonth: true
      });
    }

    // Calculate how many rows we need (minimum 5, maximum 6)
    const totalCells = days.length <= 35 ? 35 : 42;
    
    // Fill remaining cells with days from next month
    let nextMonthDay = 1;
    while (days.length < totalCells) {
      days.push({
        date: new Date(year, month + 1, nextMonthDay),
        isCurrentMonth: false
      });
      nextMonthDay++;
    }

    return days;
  };

  const getEntriesForDate = (date) => {
    if (!date) return [];
    return entries.filter(entry => {
      const entryDate = new Date(entry.date);
      return (
        entryDate.getDate() === date.getDate() &&
        entryDate.getMonth() === date.getMonth() &&
        entryDate.getFullYear() === date.getFullYear()
      );
    });
  };

  // Get scheduled entries for a specific date
  const getScheduledEntriesForDate = (date) => {
    if (!date) return [];
    return entries.filter(entry => {
      if (entry.status !== 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ' || !entry.scheduled_date) return false;
      const schedDate = new Date(entry.scheduled_date);
      return (
        schedDate.getDate() === date.getDate() &&
        schedDate.getMonth() === date.getMonth() &&
        schedDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const isHearingEntry = (entry) => {
    // Έλεγχος αν η εγγραφή αφορά δικάσιμο - με βάση τον τίτλο ή το entry_type
    if (entry.entry_type === 'hearing') return true;
    if (!entry.title) return false;
    const normalizedTitle = entry.title
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    return normalizedTitle === 'ΔΙΚΑΣΙΜΟΣ';
  };

  const countEntriesByType = (dayEntries, scheduledEntries) => {
    const hearingCount = dayEntries.filter(e => isHearingEntry(e)).length;
    const otherCount = dayEntries.length - hearingCount;
    const scheduledCount = scheduledEntries ? scheduledEntries.length : 0;
    return { hearingCount, otherCount, scheduledCount };
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return 'ΑΓΝΩΣΤΟΣ';
    const nm = client.client_type === 'natural'
      ? `${client.surname} ${client.name}`
      : client.company_name;
    return (nm || 'ΑΓΝΩΣΤΟΣ').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  const timeOf = (e) => {
    const r = (e.date || '').slice(11, 16);
    return /^\d{2}:\d{2}$/.test(r) && r !== '00:00' ? r : null;
  };

  const buildDayChips = (dayEntries, scheduledEntries) => {
    const norm = (s) => (s || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const chips = [];
    dayEntries.forEach((e) => {
      const client = getClientName(e.client_id);
      const t = timeOf(e);
      if (isHearingEntry(e)) {
        chips.push({ cls: 'bg-amber-50 border-amber-300 text-amber-900', top: `${t ? `${t} · ` : ''}Δικάσιμος`, sub: client });
      } else if (e.entry_type === 'other_activity' && norm(e.title).includes('ΡΑΝΤΕΒΟΥ')) {
        chips.push({ cls: 'bg-amber-50 border-amber-300 text-slate-800', top: `${t ? `${t} · ` : ''}${client}`, sub: 'Ραντεβού' });
      } else {
        chips.push({ cls: 'bg-white border-red-200 text-red-800', top: client, sub: capitalizeFirst(e.title) });
      }
    });
    (scheduledEntries || []).forEach((e) => {
      chips.push({ cls: 'bg-amber-50 border-amber-400 text-amber-800', top: getClientName(e.client_id), sub: capitalizeFirst(e.title) });
    });
    return chips;
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSelectedDate = (date) => {
    if (!date || !selectedDate) return false;
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  const handleDateClick = (date, isCurrentMonth) => {
    if (!date) return;
    // Only allow clicking on current month days
    if (!isCurrentMonth) return;
    // If clicking the same date, deselect it
    if (isSelectedDate(date)) {
      onDateSelect(null);
    } else {
      onDateSelect(date);
    }
  };

  const previousMonth = () => {
    const y = window.scrollY;
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    // Restore scroll synchronously after React commits the new month
    requestAnimationFrame(() => window.scrollTo({ top: y }));
  };

  const nextMonth = () => {
    const y = window.scrollY;
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    requestAnimationFrame(() => window.scrollTo({ top: y }));
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className="bg-white border border-stone-300 rounded-xl shadow-md p-6" data-testid="calendar-view">
      {/* Calendar Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex flex-col gap-2">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="prev-month-btn"
            variant="outline"
            size="icon"
            onClick={previousMonth}
            className="h-9 w-9 border-stone-300 hover:bg-stone-100 hover:border-stone-400 text-slate-700"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            data-testid="next-month-btn"
            variant="outline"
            size="icon"
            onClick={nextMonth}
            className="h-9 w-9 border-stone-300 hover:bg-stone-100 hover:border-stone-400 text-slate-700"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="w-full">
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-px mb-2">
          {dayNames.map((day) => (
            <div
              key={day}
              className="text-center text-sm font-semibold text-slate-700 py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 auto-rows-fr gap-px bg-slate-200 rounded-lg overflow-hidden" style={{minHeight: '520px'}}>
          {days.map((dayObj, index) => {
            const date = dayObj.date;
            const isCurrentMonth = dayObj.isCurrentMonth;
            const dayEntries = isCurrentMonth ? getEntriesForDate(date) : [];
            const scheduledEntries = isCurrentMonth ? getScheduledEntriesForDate(date) : [];
            const isTodayCell = isToday(date);
            const isSelected = isSelectedDate(date);
            const hasAnyEntries = dayEntries.length > 0 || scheduledEntries.length > 0;

            return (
              <div
                key={index}
                data-testid={`calendar-day-${date.getDate()}`}
                onClick={() => handleDateClick(date, isCurrentMonth)}
                onMouseEnter={(e) => {
                  if (!isCurrentMonth || !hasAnyEntries) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const above = rect.bottom > window.innerHeight - 240;
                  setHoverInfo({
                    date,
                    dayEntries,
                    scheduledEntries,
                    x: Math.max(8, Math.min(rect.left, window.innerWidth - 300)),
                    y: above ? rect.top - 8 : rect.bottom + 8,
                    above,
                  });
                }}
                onMouseLeave={() => setHoverInfo(null)}
                className={`p-1.5 sm:p-2 relative flex flex-col ${
                  isCurrentMonth 
                    ? 'bg-white hover:bg-stone-50 transition-colors cursor-pointer' 
                    : 'bg-stone-50'
                } ${isTodayCell && isCurrentMonth ? 'ring-2 ring-slate-900 ring-inset' : ''} ${
                  isSelected && isCurrentMonth && !isTodayCell ? 'bg-stone-100 ring-2 ring-slate-400 ring-inset' : ''
                }`}
              >
                <div
                  className={`font-semibold mb-1 text-sm ${
                    isCurrentMonth
                      ? (isTodayCell ? 'text-slate-900' : isSelected ? 'text-slate-900' : 'text-slate-700')
                      : 'text-slate-400'
                  }`}
                >
                  {date.getDate()}
                </div>
                {isCurrentMonth && hasAnyEntries && (
                  <div className="mt-0.5 space-y-0.5 overflow-hidden">
                    {(() => {
                      const chips = buildDayChips(dayEntries, scheduledEntries);
                      const shown = chips.slice(0, 2);
                      return (
                        <>
                          {shown.map((c, i) => (
                            <div key={i} className={`px-1 py-0.5 rounded border text-[10px] ${c.cls}`} style={{ lineHeight: 1.25 }}>
                              <p className="font-semibold truncate">{c.top}</p>
                              {c.sub && <p className="truncate opacity-80">{c.sub}</p>}
                            </div>
                          ))}
                          {chips.length > 2 && (
                            <p className="text-[10px] text-slate-500 font-medium pl-0.5">+{chips.length - 2} ακόμη</p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Hover preview: όλες οι εγγραφές της ημέρας χωρίς κλικ */}
      {hoverInfo && (
        <div
          data-testid="calendar-hover-popover"
          className="fixed z-50 w-72 bg-white border border-stone-300 rounded-xl shadow-xl p-3 pointer-events-none"
          style={{
            left: hoverInfo.x,
            top: hoverInfo.above ? undefined : hoverInfo.y,
            bottom: hoverInfo.above ? window.innerHeight - hoverInfo.y : undefined,
          }}
        >
          <p className="text-xs font-bold text-slate-900 capitalize mb-2 pb-1.5 border-b border-stone-100">
            {hoverInfo.date.toLocaleDateString('el-GR', { weekday: 'long', day: '2-digit', month: '2-digit' })}
          </p>
          <div className="space-y-1.5 max-h-56 overflow-hidden">
            {(() => {
              const rows = [
                ...hoverInfo.dayEntries.filter((e) => isHearingEntry(e)).map((e) => ({ e, color: 'bg-amber-900', label: 'Δικάσιμος' })),
                ...hoverInfo.dayEntries.filter((e) => !isHearingEntry(e)).map((e) => ({ e, color: 'bg-red-700', label: capitalizeFirst(e.title) })),
                ...hoverInfo.scheduledEntries.map((e) => ({ e, color: 'bg-amber-500', label: capitalizeFirst(e.title) })),
              ];
              const shown = rows.slice(0, 7);
              return (
                <>
                  {shown.map(({ e, color, label }, i) => (
                    <div key={`${e.id}-${i}`} className="flex items-start gap-1.5">
                      <div className={`w-2 h-2 rounded-full ${color} shrink-0 mt-1`}></div>
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-slate-900 truncate" style={{ lineHeight: 1.3 }}>
                          {(getClientName(e.client_id) || 'Χωρίς πελάτη').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')}
                        </p>
                        <p className="text-xs text-slate-600 truncate" style={{ lineHeight: 1.3 }}>{label}</p>
                      </div>
                    </div>
                  ))}
                  {rows.length > 7 && (
                    <p className="text-[11px] text-slate-400 pl-3.5">+{rows.length - 7} ακόμη — κλικ για όλες</p>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;